import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ProductCard from '../components/ProductCard';
import Pagination from '../components/Pagination';
import { supabase } from '../supabaseClient';

const CategoryPage = ({ onAddToCart, onProductClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categorySlug, subcategorySlug } = useParams();

  // ===== Query param page =====
  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const safeInitialPage =
    Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1;

  // ===== State =====
  const [currentPage, setCurrentPage] = useState(safeInitialPage);

  const [categoryId, setCategoryId] = useState(null);
  const [subcategoryId, setSubcategoryId] = useState(null);

  const [categoryMap, setCategoryMap] = useState({}); // { [catId]: slug }
  const [subcatMap, setSubcatMap] = useState({});     // { [subId]: slug }

  // Para sidebar y selector móvil
  const [availableSubcats, setAvailableSubcats] = useState([]);

  // Datos que vienen de DB
  const [products, setProducts] = useState([]);

  // total real para paginación
  const [totalProducts, setTotalProducts] = useState(0);

  // filtros UI
  const [sortBy, setSortBy] = useState('alpha_asc'); // 'alpha_asc' | 'alpha_desc' | 'price_asc' | 'price_desc'
  const [pageSize, setPageSize] = useState(15); // cuántos por página

  const [loading, setLoading] = useState(true);

  // =========================================================
  // 1. Resolver slugs → IDs y armar mapas categoryMap/subcatMap
  // =========================================================
  useEffect(() => {
    const resolveSlugs = async () => {
      setLoading(true);

      try {
        // Todas las categorías activas
        const { data: cats, error: catsErr } = await supabase
          .from('categories')
          .select('id, slug, name, is_active')
          .eq('is_active', true);

        if (catsErr) {
          console.error('Error cargando categorías:', catsErr.message);
        }

        // encontrar la categoría actual por slug
        let foundCategoryId = null;
        const catMapTmp = {};
        const catNameMapTmp = {}; // opcional si luego querés mostrar nombre bonito
        (cats || []).forEach((c) => {
          if (!c || c.id == null) return;
          catMapTmp[c.id] = c.slug;
          catNameMapTmp[c.id] = c.name || c.slug;
          if (c.slug === categorySlug) {
            foundCategoryId = c.id;
          }
        });

        // Subcategorías activas de esa categoría
        let subs = [];
        if (foundCategoryId != null) {
          const { data: subsData, error: subsErr } = await supabase
            .from('subcategories')
            .select('id, slug, name, category_id, is_active, sort_order')
            .eq('category_id', foundCategoryId)
            .eq('is_active', true)
            .order('sort_order', { ascending: true });

          if (subsErr) {
            console.error('Error cargando subcategorías:', subsErr.message);
          }
          subs = subsData || [];
        }

        // construir mapa subcatId -> slug
        const subMapTmp = {};
        let foundSubcategoryId = null;
        subs.forEach((s) => {
          if (!s || s.id == null) return;
          subMapTmp[s.id] = s.slug;
          if (subcategorySlug && s.slug === subcategorySlug) {
            foundSubcategoryId = s.id;
          }
        });

        setCategoryMap(catMapTmp);
        setSubcatMap(subMapTmp);

        setCategoryId(foundCategoryId);
        setSubcategoryId(foundSubcategoryId || null);

        // guardar lista de subcats (para sidebar y select móvil)
        // las ordenamos por sort_order asc y luego name
        const orderedSubs = [...subs].sort(
          (a, b) =>
            (a.sort_order ?? 999) - (b.sort_order ?? 999) ||
            (a.name || '').localeCompare(b.name || '')
        );
        setAvailableSubcats(orderedSubs);
      } catch (err) {
        console.error('Error en resolveSlugs:', err);
      } finally {
        setLoading(false);
      }
    };

    resolveSlugs();
  }, [categorySlug, subcategorySlug]);

  // =========================================================
  // 2. Cargar productos según categoryId/subcategoryId + paginación
  //    (y todavía sin ordenar, el orden lo aplicamos en memoria)
  // =========================================================
  useEffect(() => {
    const fetchProductsPage = async () => {
      if (categoryId == null) return;

      setLoading(true);

      try {
        // si el user seleccionó "Todos", queremos traer TODO para poder paginar front?
        // Vamos a optimizar: si pageSize === 'all' traemos todo sin .range
        const useRange = pageSize !== 'all';

        // cálculos rango
        const from = useRange ? (currentPage - 1) * pageSize : 0;
        const to = useRange ? from + pageSize - 1 : 9999; // grande para traer todo

        let query = supabase
          .from('products')
          .select('*', { count: 'exact' })
          .eq('category_id', categoryId);

        if (subcategoryId != null) {
          query = query.eq('subcategory_id', subcategoryId);
        }

        // orden base por created_at desc (nuevo primero)
        query = query.order('created_at', { ascending: false });

        if (useRange) {
          query = query.range(from, to);
        }

        const { data, error, count } = await query;

        if (error) {
          console.error('Error al obtener productos:', error.message);
          setProducts([]);
          setTotalProducts(0);
        } else {
          setProducts(data || []);
          setTotalProducts(count || 0);
        }
      } catch (err) {
        console.error('Error inesperado al cargar productos:', err);
        setProducts([]);
        setTotalProducts(0);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsPage();
  }, [categoryId, subcategoryId, currentPage, pageSize]);

  // =========================================================
  // 3. Sincronizar la URL (?page=) cuando cambia currentPage
  // =========================================================
  useEffect(() => {
    const basePath = subcategorySlug
      ? `/${categorySlug}/${subcategorySlug}`
      : `/${categorySlug}`;

    const params = new URLSearchParams(location.search);
    const currentInUrl = parseInt(params.get('page') || '1', 10);
    const safePage = Number.isFinite(currentInUrl) ? currentInUrl : 1;

    if (safePage !== currentPage) {
      params.set('page', String(currentPage));
      navigate(`${basePath}?${params.toString()}`, { replace: true });
      // subimos top cuando cambiamos de página
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage, categorySlug, subcategorySlug, navigate, location.search]);

  // =========================================================
  // 4. Cambiar subcategoría (sidebar o select móvil)
  // =========================================================
  const goToSubcategory = (newSubSlug) => {
    const base = `/${categorySlug}`;
    // reset page=1 cuando cambio filtro
    if (newSubSlug && newSubSlug.length > 0) {
      navigate(`${base}/${encodeURIComponent(newSubSlug)}?page=1`);
    } else {
      navigate(`${base}?page=1`);
    }
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // =========================================================
  // 5. Contador y ordenamiento en memoria
  // =========================================================
  // OJO: products ya viene paginado del backend; el contador real es totalProducts.
  const productosLabel = totalProducts === 1 ? 'artículo' : 'artículos';

  const sortedProducts = useMemo(() => {
    const arr = [...products];

    const getName = (p) => (p?.name || p?.title || '').toString();
    const getPrice = (p) => {
      const val = p?.price ?? p?.precio ?? 0;
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    };

    switch (sortBy) {
      case 'alpha_desc':
        return arr.sort((a, b) => getName(b).localeCompare(getName(a)));
      case 'price_asc':
        return arr.sort((a, b) => getPrice(a) - getPrice(b));
      case 'price_desc':
        return arr.sort((a, b) => getPrice(b) - getPrice(a));
      case 'alpha_asc':
      default:
        return arr.sort((a, b) => getName(a).localeCompare(getName(b)));
    }
  }, [products, sortBy]);

  // cuántas páginas totales (cuando NO es "Todos")
  const totalPages = useMemo(() => {
    if (pageSize === 'all') return 1;
    if (!totalProducts || !pageSize || pageSize <= 0) return 1;
    return Math.ceil(totalProducts / pageSize);
  }, [totalProducts, pageSize]);

  // =========================================================
  // 6. Helpers UI
  // =========================================================
  // heading grande
  const headingText = useMemo(() => {
    if (subcategorySlug) {
      return decodeURIComponent(subcategorySlug);
    }
    return decodeURIComponent(categorySlug);
  }, [categorySlug, subcategorySlug]);

  // construcción de basePath para cada producto al hacer click
  const getBasePathForProduct = (p) => {
    const catSlug = categoryMap[p.category_id];
    const subSlug = subcatMap[p.subcategory_id];

    if (catSlug && subSlug) {
      return `/${catSlug}/${subSlug}`;
    }
    if (catSlug) {
      return `/${catSlug}`;
    }
    return '/tecnologia';
  };

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
      {/* Encabezado + filtros superiores */}
      <div className="mb-8 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 capitalize">
              {headingText}
            </h1>

            <p className="text-gray-600 text-sm mt-1 flex flex-wrap items-center gap-2">
              {subcategorySlug ? (
                <>
                  <span>{decodeURIComponent(categorySlug)}</span>
                  <span>/</span>
                  <span>{decodeURIComponent(subcategorySlug)}</span>
                </>
              ) : (
                <span>{decodeURIComponent(categorySlug)}</span>
              )}

              {/* contador pill luminosa */}
              <span className="relative inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold
                       bg-gradient-to-b from-white/85 via-white/60 to-white/35
                       ring-1 ring-inset ring-white/70 border border-white/50
                       shadow-[0_6px_20px_rgba(0,0,0,0.06)] text-neutral-900">
                <span
                  aria-hidden="true"
                  className="absolute -inset-px rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.9),rgba(255,255,255,0.25)_40%,rgba(255,255,255,0)_100%)]"
                />
                <span
                  aria-hidden="true"
                  className="absolute inset-[1px] rounded-full shadow-[inset_0_1px_0_rgba(255,255,255,0.9),inset_0_-1px_0_rgba(255,255,255,0.35)]"
                />
                <span className="relative h-2 w-2 rounded-full bg-brand" aria-hidden="true" />
                <span className="relative tabular-nums">
                  {totalProducts.toLocaleString()}
                </span>
                <span className="relative opacity-70">{productosLabel}</span>
              </span>
            </p>
          </div>

          {/* FILTROS SUPERIORES */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:items-end">
          {/* Selector subcategoría (solo visible en mobile < md) */}
          <div className="block md:hidden">
            {availableSubcats.length > 0 && (
              <div className="flex flex-col">
                <label
                  htmlFor="subcategoryFilterMobile"
                  className="text-sm font-medium text-gray-700 mb-1"
                >
                  Subcategoría
                </label>
                <select
                  id="subcategoryFilterMobile"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand bg-white text-gray-800"
                  value={subcategorySlug || ''}
                  onChange={(e) => {
                    const newSlug = e.target.value;
                    goToSubcategory(newSlug);
                  }}
                >
                  <option value="">
                    Todas en {decodeURIComponent(categorySlug)}
                  </option>
                  {availableSubcats.map((sc) => (
                    <option key={sc.id} value={sc.slug}>
                      {sc.name || sc.slug}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Mostrar X por página */}
          <div className="flex flex-col">
            <label
              htmlFor="pageSizeSel"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              Mostrar
            </label>
            <select
              id="pageSizeSel"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand bg-white text-gray-800 text-sm"
              value={pageSize}
              onChange={(e) => {
                const val = e.target.value === 'all'
                  ? 'all'
                  : parseInt(e.target.value, 10);

                setPageSize(val);
                setCurrentPage(1);
              }}
            >
              <option value={15}>15</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value="all">Todos</option>
            </select>
          </div>

          {/* Ordenar */}
          <div className="flex flex-col">
            <label
              htmlFor="sortSel"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              Ordenar
            </label>
            <select
              id="sortSel"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand bg-white text-gray-800 text-sm"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="alpha_asc">Alfabético A → Z</option>
              <option value="alpha_desc">Alfabético Z → A</option>
              <option value="price_asc">Precio: menor → mayor</option>
              <option value="price_desc">Precio: mayor → menor</option>
            </select>
          </div>
        </div>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL: sidebar izquierda (md+) + grid productos */}
      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar (desktop) */}
        <aside className="hidden md:block md:w-60 flex-shrink-0">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sticky top-[80px]">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">
              Subcategorías
            </h2>
            <ul className="space-y-2 text-sm text-gray-700">
              {/* Todas */}
              <li>
                <button
                  className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                    !subcategorySlug
                      ? 'bg-brand text-white border-brand'
                      : 'bg-white border-gray-300 hover:border-brand hover:text-brand'
                  }`}
                  onClick={() => goToSubcategory('')}
                >
                  Todas en {decodeURIComponent(categorySlug)}
                </button>
              </li>

              {availableSubcats.map((sc) => (
                <li key={sc.id}>
                  <button
                    className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                      subcategorySlug === sc.slug
                        ? 'bg-brand text-white border-brand'
                        : 'bg-white border-gray-300 hover:border-brand hover:text-brand'
                    }`}
                    onClick={() => goToSubcategory(sc.slug)}
                  >
                    {sc.name || sc.slug}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Grid productos */}
        <section className="flex-1">
          {loading ? (
            <p className="text-gray-600 text-center py-12">Cargando...</p>
          ) : sortedProducts.length === 0 ? (
            <p className="text-gray-600 text-center py-12">
              No hay productos en esta categoría.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
                {sortedProducts.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onAddToCart={onAddToCart}
                    onProductClick={() => {
                      const basePath = getBasePathForProduct(p);
                      onProductClick(p.id, basePath);
                    }}
                  />
                ))}
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="mt-10">
                  <Pagination
                    productsPerPage={pageSize === 'all' ? totalProducts : pageSize}
                    totalProducts={totalProducts}
                    paginate={(pageNum) => {
                      setCurrentPage(pageNum);
                    }}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default CategoryPage;
